'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { styles } from './FormMessage.styles';

const DEFAULT_DURATION_MS = 3000;

export interface FormMessageProps {
  message: string;
  status: 'success' | 'error';
  /** Через сколько мс запустить скрытие (по умолчанию 3000). 0 — не скрывать автоматически */
  duration?: number;
  /** Вызывается после завершения анимации скрытия */
  onDismiss?: () => void;
  className?: string;
}

/** Блок для вывода сообщения формы (ошибка/успех) внутри компонента. Появление снизу из прозрачности, автоскрытие по таймеру */
export function FormMessage({
  message,
  status,
  duration = DEFAULT_DURATION_MS,
  onDismiss,
  className
}: FormMessageProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [isExiting, setIsExiting] = useState(false);
  const onDismissRef = useRef(onDismiss);
  const dismissedRef = useRef(false);
  onDismissRef.current = onDismiss;
  const slots = styles({ status });

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    el.style.setProperty('--form-message-height', `${el.offsetHeight}px`);
  }, [message]);

  useEffect(() => {
    if (duration <= 0 || !onDismiss) return;
    const timer = setTimeout(() => setIsExiting(true), duration);
    return () => clearTimeout(timer);
  }, [duration, onDismiss]);

  const handleAnimationEnd = () => {
    if (isExiting && !dismissedRef.current) {
      dismissedRef.current = true;
      onDismissRef.current?.();
    }
  };

  return (
    <div
      ref={rootRef}
      className={slots.base({ className })}
      data-state={isExiting ? 'closed' : 'open'}
      role="alert"
      aria-live="polite"
      onAnimationEnd={handleAnimationEnd}
    >
      <div className={slots.message()}>{message}</div>
    </div>
  );
}
